<template>
  <div
    class="notification-card border-start border-4 p-1 rounded-3 shadow-sm mb-2 hover-bg-light transition-all cursor-pointer position-relative"
    :class="cardClasses"
    @click="viewDetail(notification)"
  >
    <button
      class="btn-close-notification text-muted border-0 bg-transparent p-0 position-absolute top-0 end-0 m-2"
      style="z-index: 10"
      @click.stop="removeNotification(notification)"
    >
      <X :size="14" />
    </button>
    <div class="d-flex align-items-center gap-1">
      <div class="flex-shrink-0 mt-1">
        <div
          class="icon-container rounded-circle d-flex align-items-center justify-content-center"
          :class="iconClasses"
        >
          <RotateCcw v-if="isReset" :size="16" stroke-width="3" />
          <CheckCircle v-else-if="isApproved" :size="16" stroke-width="3" />
          <AlertCircle v-else :size="16" stroke-width="3" />
        </div>
      </div>
      <div class="flex-grow-1 min-w-0">
        <div class="d-flex justify-content-between align-items-start mb-1">
          <span
            class="px-1 fw-bold fs-10 whitespace-nowrap text-uppercase rounded-pill"
            :class="badgeClasses"
          >
            {{ statusText }}
          </span>
        </div>

        <h6 class="fs-14 fw-bold text-dark mb-1 text-truncate font-gotham-bold">
          {{ displayTitle }}
        </h6>
        <p v-if="displayMessage">{{ displayMessage }}</p>

        <p
          v-if="notification.status_comment"
          class="fs-12 text-secondary mb-0 fst-italic line-clamp-2 lh-sm"
        >
          "{{ notification.status_comment }}"
        </p>
      </div>
    </div>
    <div class="d-flex justify-content-end align-items-center gap-2">
      <span class="text-muted fs-10 fw-medium whitespace-nowrap">
        {{ notification.time }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { AlertCircle, CheckCircle, RotateCcw, X } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['view-detail', 'remove'])

const isRejected = computed(() => {
  return (
    props.notification.type === 'correlative_rejected' || props.notification.status === 'rejected'
  )
})

const isReset = computed(() => {
  return props.notification.type === 'correlative_reset' || props.notification.status === 'reset'
})

const isApproved = computed(() => {
  return (
    props.notification.type === 'correlative_approved' || props.notification.status === 'approved'
  )
})

const cardClasses = computed(() => {
  if (isRejected.value) return 'border-danger bg-danger-transparent'
  if (isReset.value) return 'border-warning bg-warning-transparent'
  if (isApproved.value) return 'border-success bg-success-transparent'
  return 'border-primary bg-primary-transparent'
})

const iconClasses = computed(() => {
  if (isRejected.value) return 'bg-danger text-white'
  if (isReset.value) return 'bg-warning text-white'
  if (isApproved.value) return 'bg-success text-white'
  return 'bg-primary text-white'
})

const badgeClasses = computed(() => {
  if (isRejected.value) return 'bg-danger-transparent text-danger'
  if (isReset.value) return 'bg-warning-transparent text-warning'
  if (isApproved.value) return 'bg-success-transparent text-success'
  return 'bg-primary-transparent text-primary'
})

const statusText = computed(() => {
  if (isRejected.value) return t('notifications.correlative_rejected')
  if (isReset.value) return t('notifications.correlative_reset')
  if (isApproved.value) return t('notifications.correlative_approved')
  if (documentCode.value !== '---') return t('notifications.correlative_notification')

  return t('notifications.new') || 'Notificación'
})

const documentCode = computed(() => {
  const corr = props.notification?.correlative
  // Try to find document code in common paths
  return (
    props?.notification?.code ||
    corr?.project_document?.code ||
    corr?.code ||
    corr?.document?.code ||
    corr?.correlative ||
    '---'
  )
})

const displayTitle = computed(() => {
  if (documentCode.value !== '---') {
    return `${t('quality.document') || 'Documento'}: ${documentCode.value}`
  }
  return props.notification.title || 'Notificación'
})

const displayMessage = computed(() => {
  const msg = props.notification.message
  // If title and message are identical and we are showing title in header, avoid duplication
  if (documentCode.value === '---' && props.notification.title === msg) {
    return null
  }
  return msg
})

/**
 * Emits the remove event with the notification object.
 * @param notification
 */
const removeNotification = (notification) => {
  emit('remove', notification)
}

/**
 * Emits the view-detail event with the notification object.
 * @param notification
 */
const viewDetail = (notification) => {
  emit('view-detail', notification)
}
</script>

<style scoped>
.notification-card {
  max-width: 100%;
  border-left-width: 4px !important;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  white-space: normal;
}

.notification-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.bg-danger-transparent {
  background-color: rgba(235, 71, 71, 0.1) !important;
}
.bg-warning-transparent {
  background-color: rgba(255, 193, 7, 0.1) !important;
}
.bg-primary-transparent {
  background-color: rgba(13, 110, 253, 0.1) !important;
}
.bg-success-transparent {
  background-color: rgba(25, 135, 84, 0.1) !important;
}

.text-warning {
  color: #ffc107 !important;
}
.text-success {
  color: #198754 !important;
}

.btn-close-notification {
  opacity: 0.5;
  transition: opacity 0.2s;
}
.btn-close-notification:hover {
  opacity: 1;
}

.icon-container {
  width: 28px;
  height: 28px;
}
.avatar-user {
  width: 20px;
  height: 20px;
  border: 1px solid #eee;
}

.fs-10 {
  font-size: 10px;
}
.fs-11 {
  font-size: 11px;
}
.fs-12 {
  font-size: 12px;
}
.fs-14 {
  font-size: 14px;
}
.max-w-150 {
  max-width: 150px;
}
.font-gotham-bold {
  font-family: 'Gotham Rounded Bold', sans-serif;
}
.font-gotham-light {
  font-family: 'Gotham Rounded Light', sans-serif;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.tracking-wider {
  letter-spacing: 0.05em;
}
</style>
